import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Spinner,
  Alert,
  Card,
  Row,
  Col,
  Table,
  Badge,
  ButtonGroup,
  Modal,
  Form,
} from "react-bootstrap";
import { FaArrowLeft, FaFileAlt, FaBoxOpen, FaEraser } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import SuccessModal from "../components/modals/SuccessModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { useNotificationModal } from "../hooks/useNotificationModal";
import { formatPhoneWithCountryCode } from "../utils/phoneFormatter";
import {
  generatePackingLabelPDF,
  fetchCompanyLogo,
} from "../utils/packingLabelPDF";
import "../styles/WarehouseIssuing.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function OrderDetails() {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stockData, setStockData] = useState({});

  // Column-level filters for order items table
  const [orderItemsColumnFilter, setOrderItemsColumnFilter] = useState({
    index: "",
    sku: "",
    style: "",
    color: "",
    size: "",
    fit: "",
    material: "",
    quantity: "",
    sale_price: "",
    total_price: "",
    available_stock: "",
    status: "",
  });

  // Helper: case-insensitive substring match
  const matches = (value, filter) => {
    if (!filter) return true;
    const val = value === null || value === undefined ? "" : String(value);
    return val.toLowerCase().includes(filter.toLowerCase());
  };

  // Clear all search/filter inputs
  const clearSearchBar = () => {
    setOrderItemsColumnFilter({
      index: "",
      sku: "",
      style: "",
      color: "",
      size: "",
      fit: "",
      material: "",
      quantity: "",
      sale_price: "",
      total_price: "",
      available_stock: "",
      status: "",
    });
  };

  // Modal states
  const [showIssuingModal, setShowIssuingModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showIssueAllModal, setShowIssueAllModal] = useState(false);
  const [issuingAll, setIssuingAll] = useState(false);

  // Fetch order details
  useEffect(() => {
    if (!company_code || !order_id) return;

    setLoading(true);
    fetch(
      `${BASE_URL}/api/admin/issuing/orders/${order_id}?company_code=${company_code}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setOrder(data.order);

          // Map items and set issued batch information if available
          const itemsWithIssuedBatch = (data.items || []).map((item) => {
            // If item has been issued, use the actual issued batch information
            if (item.booking_status === "Issued" && item.issued_batch_number) {
              return {
                ...item,
                selected_batch: {
                  batch_number: item.issued_batch_number,
                  lot_no: item.issued_lot_no,
                  unit_price: item.issued_unit_price,
                  main_stock_qty: item.issued_batch_stock_qty,
                },
              };
            }
            return item;
          });

          setOrderItems(itemsWithIssuedBatch);

          // Extract shipping address from order details
          if (data.order) {
            setShippingAddress({
              shipping_first_name: data.order.shipping_first_name,
              shipping_last_name: data.order.shipping_last_name,
              address_line_1: data.order.address_line_1,
              address_line_2: data.order.address_line_2,
              city: data.order.city,
              state: data.order.state,
              postal_code: data.order.postal_code,
              country: data.order.country,
              country_code: data.order.country_code,
              phone: data.order.shipping_phone,
            });
          }
          // Fetch stock data for all items
          fetchStockData(itemsWithIssuedBatch);
        } else {
          setError(data.error || "Failed to load order details");
          showError(data.error || "Failed to load order details", "Error");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load order details");
        showError("Failed to load order details", "Error");
        setLoading(false);
      });
  }, [company_code, order_id, showError]);

  // Fetch stock data for order items
  const fetchStockData = async (items) => {
    if (!company_code || items.length === 0) return;

    try {
      // Only fetch stock for items that haven't been issued yet
      const itemsNeedingStock = items.filter(
        (item) => item.booking_status !== "Issued"
      );

      if (itemsNeedingStock.length === 0) {
        // All items are issued, just set stock data from issued batches
        const stockMap = {};
        items.forEach((item) => {
          if (
            item.selected_batch &&
            item.selected_batch.main_stock_qty !== undefined
          ) {
            stockMap[item.sku] = item.selected_batch.main_stock_qty;
          }
        });
        setStockData(stockMap);
        return;
      }

      // For each item fetch available-stock and also pick up the default FIFO selected_stock
      const stockPromises = itemsNeedingStock.map(async (item) => {
        try {
          const response = await fetch(
            `${BASE_URL}/api/admin/issuing/available-stock?company_code=${company_code}&style_number=${item.style_number}&sku=${item.sku}`
          );
          const data = await response.json();

          // Calculate total stock from available_stocks array
          let totalStock = 0;
          if (
            data?.success &&
            data?.data?.available_stocks &&
            Array.isArray(data.data.available_stocks)
          ) {
            totalStock = data.data.available_stocks.reduce((sum, stock) => {
              return sum + (parseInt(stock.main_stock_qty) || 0);
            }, 0);
          }

          // selected_stock may be provided by the API as the FIFO/default stock for this sku
          const selected_stock = data?.success
            ? data.data.selected_stock
            : null;

          return {
            sku: item.sku,
            stock_qty: totalStock,
            selected_stock,
          };
        } catch (error) {
          console.error(`Failed to fetch stock for ${item.sku}:`, error);
          return {
            sku: item.sku,
            stock_qty: 0,
            selected_stock: null,
          };
        }
      });

      const stockResults = await Promise.all(stockPromises);
      const stockMap = {};
      const selectedStockMap = {};

      stockResults.forEach((result) => {
        stockMap[result.sku] = result.stock_qty;
        if (result.selected_stock)
          selectedStockMap[result.sku] = result.selected_stock;
      });

      // Add issued item stock to map
      items.forEach((item) => {
        if (
          item.booking_status === "Issued" &&
          item.selected_batch &&
          item.selected_batch.main_stock_qty !== undefined
        ) {
          stockMap[item.sku] = item.selected_batch.main_stock_qty;
        }
      });

      setStockData(stockMap);

      // Attach FIFO selected_stock to order items as `selected_batch` only if an item does not already have a selection
      setOrderItems((prevItems) =>
        prevItems.map((item) => {
          if (item.selected_batch) return item; // keep user's existing selection or issued batch
          const fifo = selectedStockMap[item.sku] || null;
          return fifo ? { ...item, selected_batch: fifo } : item;
        })
      );
    } catch (error) {
      console.error("Failed to fetch stock data:", error);
    }
  };

  // Open confirm modal to issue all items
  const handleIssueAllItems = () => {
    setShowIssueAllModal(true);
  };

  const closeIssueAllModal = () => {
    setShowIssueAllModal(false);
  };

  const confirmIssueAll = async () => {
    setIssuingAll(true);

    try {
      // Filter out already issued items and prepare issuing data
      const availableItems = orderItems.filter(
        (item) => item.booking_status !== "Issued"
      );
      if (availableItems.length === 0) {
        showError("No items available for issuing.", "Validation Error");
        return;
      }

      // Check if all items have sufficient stock before proceeding
      const insufficientStockItems = availableItems.filter(
        (item) =>
          stockData[item.sku] !== undefined &&
          stockData[item.sku] < item.quantity
      );

      if (insufficientStockItems.length > 0) {
        const itemsList = insufficientStockItems
          .map(
            (item) =>
              `${item.sku} (Required: ${item.quantity}, Available: ${stockData[item.sku]
              })`
          )
          .join("\n");
        showError(
          `Insufficient stock for the following items:\n${itemsList}`,
          "Insufficient Stock"
        );
        return;
      }

      // Prepare issuing items
      const issuingItems = availableItems.map((item) => {
        const issuing = {
          order_item_id: item.order_item_id,
          style_number: item.style_number,
          sku: item.sku,
          issuing_qty: item.quantity,
          color_name: item.color_name || "",
          size_name: item.size_name || "",
          fit_name: item.fit_name || "",
          material_name: item.material_name || "",
        };

        const resolvedUnitPrice =
          item.selected_batch &&
            typeof item.selected_batch.unit_price !== "undefined"
            ? item.selected_batch.unit_price
            : typeof item.unit_price !== "undefined"
              ? item.unit_price
              : item.sale_price;

        issuing.unit_price =
          typeof resolvedUnitPrice !== "undefined" && resolvedUnitPrice !== null
            ? parseFloat(resolvedUnitPrice)
            : 0;

        issuing.sale_price =
          typeof item.sale_price !== "undefined" && item.sale_price !== null
            ? parseFloat(item.sale_price)
            : 0;

        if (item.selected_batch) {
          issuing.selected_batch = {
            batch_number: item.selected_batch.batch_number,
            lot_no: item.selected_batch.lot_no,
            unit_price: item.selected_batch.unit_price,
            sale_price: item.selected_batch.sale_price || null,
          };
        }

        return issuing;
      });

      const issuedByUserId = userData?.id || "SYSTEM";

      const res = await fetch(
        `${BASE_URL}/api/admin/issuing/orders/${order.order_id}/issue-all`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_code: company_code,
            order_id: order.order_id,
            order_number: order.order_number,
            issuing_items: issuingItems,
            issued_by: issuedByUserId,
            platform_type: order.platform_type
          }),
        }
      );

      const data = await res.json();
      if (data.success) {
        setOrderItems((prev) =>
          prev.map((item) => {
            const issuedItem = issuingItems.find(
              (issued) => issued.order_item_id === item.order_item_id
            );
            return issuedItem ? { ...item, booking_status: "Issued" } : item;
          })
        );
        showSuccess(
          `Successfully issued ${data.issued_count} items!`,
          "Success"
        );
        // Refresh stock data after issuing
        fetchStockData(orderItems);
      } else {
        showError(data.error || "Failed to issue all items.", "Error");
      }
    } catch (err) {
      console.error("Failed to issue all items:", err);
      showError("Failed to issue all items.", "Error");
    } finally {
      setIssuingAll(false);
      closeIssueAllModal();
    }
  };

  // Handle download picking list
  const handleDownloadPickingList = () => {
    if (!company_code || !order_id) return;

    const url = `${BASE_URL}/api/admin/issuing/orders/${order_id}/picking-list?company_code=${company_code}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = `PickingList-${order.order_number}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle download packing label
  const handleDownloadPackingLabel = async () => {
    if (!order) return;

    // Fetch company details
    let companyDetails = null;
    if (company_code) {
      try {
        const response = await fetch(
          `${BASE_URL}/api/admin/issuing/orders/company_details?company_code=${company_code}`
        );
        if (response.ok) {
          const data = await response.json();
          // API returns company_name, company_address, company_phone directly
          if (data.company_name || data.company_address || data.company_phone) {
            companyDetails = data;
          }
        }
      } catch (e) { }
    }

    // Prepare order data with shipping information and company details
    const orderData = {
      order_number: order.order_number,
      company_name: companyDetails?.company_name || order.company_name || " ",
      company_address: (() => {
        if (companyDetails) {
          const parts = [];
          if (companyDetails.address_line1) parts.push(companyDetails.address_line1);
          if (companyDetails.address_line2) parts.push(companyDetails.address_line2);
          if (companyDetails.city) parts.push(companyDetails.city);
          if (companyDetails.postal_code) parts.push(companyDetails.postal_code);
          if (companyDetails.country) parts.push(companyDetails.country);
          if (parts.length > 0) return parts.join(", ");
        }
        // Fallback to old order structure or empty
        return order.company_address || "";
      })(),
      company_phone: companyDetails?.company_phone || order.company_phone || "",
      company_country_code: companyDetails?.company_country_code || "",
      shipping_first_name: order.shipping_first_name || "",
      shipping_last_name: order.shipping_last_name || "",
      address_line_1: order.address_line_1 || "",
      address_line_2: order.address_line_2 || "",
      city: order.city || "",
      state: order.state || "",
      country_code: order.country_code || "",
      phone: order.shipping_phone || "",
      created_at: order.created_at || "",
    };

    // Fetch company logo
    const logoDataUrl = await fetchCompanyLogo(company_code);

    // Generate PDF with logo and company details
    generatePackingLabelPDF(orderData, orderItems, logoDataUrl);
  };

  // Handle opening stock information modal for individual item
  const handleOpenStockInfoModal = async (item) => {
    setSelectedItem(item);
    setShowIssuingModal(true);
    setModalLoading(true);

    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/issuing/available-stock?company_code=${company_code}&style_number=${item.style_number}&sku=${item.sku}`
      );
      const data = await response.json();

      if (data.success) {
        setAvailableStocks(data.data.available_stocks);

        // Keep default FIFO batch only if user not selected a batch
        if (item.selected_batch && item.selected_batch.batch_number) {
          // Find the matching batch in available stocks to get full details
          const matchingBatch = data.data.available_stocks.find(
            (stock) =>
              stock.batch_number === item.selected_batch.batch_number &&
              stock.lot_no === item.selected_batch.lot_no &&
              stock.unit_price === item.selected_batch.unit_price
          );
          setSelectedBatch(matchingBatch || item.selected_batch);
        } else {
          // No batch selected yet, use default FIFO
          setSelectedBatch(data.data.selected_stock);
        }
      } else {
        showError(data.message || "Failed to load stock data", "Error");
        setAvailableStocks([]);
        setSelectedBatch(null);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
      showError("Failed to load stock data", "Error");
      setAvailableStocks([]);
      setSelectedBatch(null);
    } finally {
      setModalLoading(false);
    }
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setShowIssuingModal(false);
    setSelectedItem(null);
    setSelectedBatch(null);
    setAvailableStocks([]);
  };

  // Apply selected batch to the corresponding order item in the table
  const handleApplyBatch = () => {
    if (!selectedItem || !selectedBatch) {
      showError("Please select a batch before applying", "Validation Error");
      return;
    }

    setOrderItems((prev) =>
      prev.map((item) =>
        item.order_item_id === selectedItem.order_item_id
          ? { ...item, selected_batch: selectedBatch }
          : item
      )
    );

    // close modal after applying
    handleCloseModal();
  };

  const handleBackToOrders = () => {
    navigate("/warehouse/issuing");
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!order) {
    return <Alert variant="warning">Order not found</Alert>;
  }

  return (
    <div className="order-details-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Order Details - {order.order_number}</h2>
        <Button
          variant="secondary"
          onClick={handleBackToOrders}
          style={{ padding: "10px" }}
        >
          <FaArrowLeft /> Back to Orders
        </Button>
      </div>

      <Row className="mb-4">
        {/* Order Information */}
        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Order Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={4}>
                  <strong>Order Number:</strong>
                  <br />
                  {order.order_number}
                </Col>
                <Col sm={4}>
                  <strong>Order Status:</strong>
                  <br />
                  {order.order_status}
                </Col>
                <Col sm={4}>
                  <strong>Platform Type:</strong>
                  <br />
                  {order.platform_type ? "Customer Site" : "Common Site"}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={6}>
                  <strong>Customer:</strong>
                  <br />
                  {order.customer_name}
                </Col>
                <Col sm={6}>
                  <strong>Email:</strong>
                  <br />
                  {order.customer_email}
                </Col>
                {/* <Row className="mt-2"> */}
                  <Col sm={6}>
                    <strong>Phone:</strong>
                    <br />
                    {formatPhoneWithCountryCode(
                      order.customer_country_code,
                      order.customer_phone,
                    )}
                  </Col>
                {/* </Row> */}
              </Row>
              <hr />
              <Row>
                <Col sm={6}>
                  <strong>Subtotal:</strong>
                  <br />${parseFloat(order.subtotal || 0).toFixed(2)}
                </Col>
                <Col sm={6}>
                  <strong>Shipping Fee:</strong>
                  <br />${parseFloat(order.shipping_fee || 0).toFixed(2)}
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={6}>
                  <strong>Tax Amount:</strong>
                  <br />${parseFloat(order.tax_amount || 0).toFixed(2)}
                </Col>
                <Col sm={6}>
                  <strong>Total Amount:</strong>
                  <br />
                  <h5 className="text-primary">
                    ${parseFloat(order.total_amount || 0).toFixed(2)}
                  </h5>
                </Col>
                <Col sm={4}>
                  <strong>Payment Method:</strong>
                  <br />
                  {(() => {
                    const pm = order.payment_method;
                    const label = pm
                      ? pm === "cod" || pm === "COD"
                        ? "Cash on Delivery"
                        : pm
                      : order.payment_method_id
                      ? "Card"
                      : "N/A";
                    return label;
                  })()}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col sm={6}>
                  <strong>Total Items:</strong>
                  <br />
                  {order.total_items}
                </Col>
                <Col sm={6}>
                  <strong>Created:</strong>
                  <br />
                  {new Date(order.created_at).toLocaleDateString()}
                </Col>
              </Row>
              {order.order_notes && (
                <>
                  <hr />
                  <div>
                    <strong>Order Notes:</strong>
                    <br />
                    {order.order_notes}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Shipping Details */}
        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Shipping Details</h5>
            </Card.Header>
            <Card.Body>
              {shippingAddress ? (
                <>
                  <div className="mb-2">
                    <strong>Full Name:</strong>
                    <br />
                    {shippingAddress.shipping_first_name}{" "}
                    {shippingAddress.shipping_last_name}
                  </div>
                  <div className="mb-2">
                    <strong>Address Line 1:</strong>
                    <br />
                    {shippingAddress.address_line_1}
                  </div>
                  {shippingAddress.address_line_2 && (
                    <div className="mb-2">
                      <strong>Address Line 2:</strong>
                      <br />
                      {shippingAddress.address_line_2}
                    </div>
                  )}
                  <Row>
                    <Col sm={6}>
                      <strong>City:</strong>
                      <br />
                      {shippingAddress.city}
                    </Col>
                    <Col sm={6}>
                      <strong>State:</strong>
                      <br />
                      {shippingAddress.state}
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col sm={6}>
                      <strong>Postal Code:</strong>
                      <br />
                      {shippingAddress.postal_code}
                    </Col>
                    <Col sm={6}>
                      <strong>Country:</strong>
                      <br />
                      {shippingAddress.country}
                    </Col>
                  </Row>
                  {shippingAddress.phone && (
                    <div className="mt-2">
                      <strong>Phone:</strong>
                      <br />
                      {formatPhoneWithCountryCode(
                        shippingAddress.country_code,
                        shippingAddress.phone,
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted">No shipping address available</div>
              )}
            </Card.Body>
          </Card>
          {order.order_status === "In Transit" && (
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0"> Issuing type</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>{order.type}</strong>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Order Items */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Order Items</h5>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleIssueAllItems}
              disabled={
                orderItems.length === 0 ||
                orderItems.every((item) => item.booking_status === "Issued") ||
                orderItems.some(
                  (item) =>
                    item.booking_status !== "Issued" &&
                    stockData[item.sku] !== undefined &&
                    stockData[item.sku] < item.quantity,
                )
              }
            >
              Issue All Items
            </Button>
            <ButtonGroup size="sm">
              <Button
                variant="primary"
                onClick={handleDownloadPickingList}
                disabled={orderItems.length === 0}
                title="Download Picking List"
              >
                <FaFileAlt className="me-1" />
                Picking List
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadPackingLabel}
                disabled={orderItems.length === 0}
                title="Download Packing Label"
              >
                <FaBoxOpen className="me-1" />
                Packing Label
              </Button>
            </ButtonGroup>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Fit</th>
                  <th>Material</th>
                  <th>Quantity</th>
                  <th>Sale Price</th>
                  <th>Total Price</th>
                  <th>Batch Number</th>
                  <th>Available Stock</th>
                </tr>
              </thead>
              <tbody>
                {/* Column-level filters row */}
                <tr className="table-filters-row">
                  <td>
                    {" "}
                    <FaEraser
                      className="pf-clear-filters-btn"
                      onClick={clearSearchBar}
                      title="Clear All Filters"
                      size={13}
                      style={{ cursor: "pointer" }}
                    />{" "}
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="SKU"
                      value={orderItemsColumnFilter.sku}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          sku: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Product"
                      value={orderItemsColumnFilter.style}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          style: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Color"
                      value={orderItemsColumnFilter.color}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          color: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Size"
                      value={orderItemsColumnFilter.size}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          size: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Fit"
                      value={orderItemsColumnFilter.fit}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          fit: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Material"
                      value={orderItemsColumnFilter.material}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          material: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Qty"
                      value={orderItemsColumnFilter.quantity}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          quantity: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Sale"
                      value={orderItemsColumnFilter.sale_price}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          sale_price: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Total"
                      value={orderItemsColumnFilter.total_price}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          total_price: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Batch Number"
                      value={orderItemsColumnFilter.selected_batch}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          selected_batch: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder="Stock"
                      value={orderItemsColumnFilter.available_stock}
                      onChange={(e) =>
                        setOrderItemsColumnFilter({
                          ...orderItemsColumnFilter,
                          available_stock: e.target.value,
                        })
                      }
                    />
                  </td>
                </tr>

                {(() => {
                  const filtered = orderItems.filter((item, idx) => {
                    if (!matches(String(idx + 1), orderItemsColumnFilter.index))
                      return false;
                    if (!matches(item.sku, orderItemsColumnFilter.sku))
                      return false;
                    if (!matches(item.style_name, orderItemsColumnFilter.style))
                      return false;
                    if (!matches(item.color_name, orderItemsColumnFilter.color))
                      return false;
                    if (!matches(item.size_name, orderItemsColumnFilter.size))
                      return false;
                    if (!matches(item.fit_name, orderItemsColumnFilter.fit))
                      return false;
                    if (
                      !matches(
                        item.material_name,
                        orderItemsColumnFilter.material,
                      )
                    )
                      return false;
                    if (
                      !matches(
                        String(item.quantity),
                        orderItemsColumnFilter.quantity,
                      )
                    )
                      return false;
                    if (
                      !matches(
                        parseFloat(item.sale_price || 0).toFixed(2),
                        orderItemsColumnFilter.sale_price,
                      )
                    )
                      return false;
                    if (
                      !matches(
                        parseFloat(item.total_price || 0).toFixed(2),
                        orderItemsColumnFilter.total_price,
                      )
                    )
                      return false;
                    const selectedBatchQty =
                      item.selected_batch &&
                      typeof item.selected_batch.main_stock_qty !== "undefined"
                        ? item.selected_batch.main_stock_qty
                        : null;
                    const availableForFilter =
                      selectedBatchQty !== null
                        ? selectedBatchQty
                        : stockData[item.sku];
                    if (
                      !matches(
                        String(
                          availableForFilter !== undefined
                            ? availableForFilter
                            : "",
                        ),
                        orderItemsColumnFilter.available_stock,
                      )
                    )
                      return false;
                    return true;
                  });

                  if (filtered.length === 0) {
                    return (
                      <tr>
                        <td colSpan={14} className="text-center">
                          No order items found.
                        </td>
                      </tr>
                    );
                  }

                  return filtered.map((item, idx) => (
                    <tr key={item.order_item_id}>
                      <td>{idx + 1}</td>
                      <td>{item.sku}</td>
                      <td>{item.style_name || "N/A"}</td>
                      <td>{item.color_name || "N/A"}</td>
                      <td>{item.size_name || "N/A"}</td>
                      <td>{item.fit_name || "N/A"}</td>
                      <td>{item.material_name || "N/A"}</td>
                      <td>{item.quantity}</td>
                      <td>${parseFloat(item.sale_price || 0).toFixed(2)}</td>
                      <td>${parseFloat(item.total_price || 0).toFixed(2)}</td>
                      <td>
                        {item.selected_batch ? (
                          <div>
                            <strong>{item.selected_batch.batch_number}</strong>
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.8em" }}
                            >
                              {/* {item.selected_batch.lot_no} */}
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted">No batch</div>
                        )}
                        {item.booking_status !== "Issued" && (
                          <Button
                            variant="outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenStockInfoModal(item);
                            }}
                            className="select-batch-button"
                          >
                            Change
                          </Button>
                        )}
                      </td>
                      <td className="text-end">
                        {(() => {
                          // Prefer selected batch available qty when a batch is chosen, otherwise show total stock
                          const selectedQty =
                            item.selected_batch &&
                            typeof item.selected_batch.main_stock_qty !==
                              "undefined"
                              ? item.selected_batch.main_stock_qty
                              : null;
                          const availableQty =
                            selectedQty !== null
                              ? selectedQty
                              : stockData[item.sku];

                          return (
                            <>
                              {typeof availableQty !== "undefined"
                                ? availableQty
                                : "Loading..."}
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Stock Information Modal */}
      <Modal show={showIssuingModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Stock Information - {selectedItem?.sku}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">Loading stock information...</p>
            </div>
          ) : (
            <>
              {selectedItem && (
                <Row className="mb-3">
                  <Col md={12}>
                    <Card>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <strong>Item Details:</strong>
                            <br />
                            SKU: {selectedItem.sku}
                            <br />
                            Product: {selectedItem.style_name || "N/A"}
                            <br />
                            Color: {selectedItem.color_name || "N/A"}
                            <br />
                            Size: {selectedItem.size_name || "N/A"}
                          </Col>
                          <Col md={6}>
                            <strong>Order Information:</strong>
                            <br />
                            Required Quantity: {selectedItem.quantity}
                            <br />
                            Status:{" "}
                            <Badge
                              bg={
                                selectedItem.booking_status === "Issued"
                                  ? "success"
                                  : selectedItem.booking_status === "Not Booked"
                                    ? "secondary"
                                    : "warning"
                              }
                            >
                              {selectedItem.booking_status || "Not Booked"}
                            </Badge>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Available Stock Selection */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">
                    Available Stock - Select Batch for Issuing
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Batch Number</th>
                          <th>Lot Number</th>
                          <th>Unit Price</th>
                          <th>Location</th>
                          <th>Available Qty</th>
                          <th>Received Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableStocks.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center">
                              No stock available for this item
                            </td>
                          </tr>
                        ) : (
                          availableStocks.map((stock, idx) => (
                            <tr
                              key={idx}
                              className={
                                selectedBatch?.batch_number ===
                                  stock.batch_number &&
                                selectedBatch?.lot_no === stock.lot_no &&
                                selectedBatch?.unit_price === stock.unit_price
                                  ? "table-primary"
                                  : ""
                              }
                            >
                              <td>
                                <Form.Check
                                  type="radio"
                                  name="selectedStock"
                                  checked={
                                    selectedBatch?.batch_number ===
                                      stock.batch_number &&
                                    selectedBatch?.lot_no === stock.lot_no &&
                                    selectedBatch?.unit_price ===
                                      stock.unit_price
                                  }
                                  onChange={() => setSelectedBatch(stock)}
                                />
                              </td>
                              <td>{stock.batch_number}</td>
                              <td>{stock.lot_no}</td>
                              <td>
                                ${parseFloat(stock.unit_price).toFixed(2)}
                              </td>
                              <td>{stock.location_name || "N/A"}</td>
                              <td>
                                <Badge
                                  bg={
                                    stock.main_stock_qty >=
                                    (selectedItem?.quantity || 0)
                                      ? "success"
                                      : "warning"
                                  }
                                >
                                  {stock.main_stock_qty}
                                </Badge>
                              </td>

                              <td>
                                {new Date(
                                  stock.created_at,
                                ).toLocaleDateString()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyBatch}
            disabled={!selectedBatch}
          >
            Apply Batch
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm Issue All Modal */}
      <ConfirmModal
        show={showIssueAllModal}
        onHide={closeIssueAllModal}
        onConfirm={confirmIssueAll}
        title="Issue All Items"
        message={`Are you sure you want to issue all available items for order ${order.order_number}? This action cannot be undone.`}
        confirmText="Confirm Issue All"
        cancelText="Cancel"
        confirmVariant="primary"
        loading={issuingAll}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
    </div>
  );
}
